import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyOSRPipe } from '@core/pipes';

@Injectable({
  providedIn: 'root',
})
export class CalcService {
   readonly #currencyPipe = inject(CurrencyOSRPipe);
   readonly #translateService = inject(TranslateService);

  /**
   * Formata o preço de um serviço, seja com a currency ou com o texto gratuito/sob consulta
   * @param price Preço do serviço
   * @param isCostless Indica se o serviço é gratuito
   * @param isPromotional Indica se o serviço está em promoção
   * @param promotionalPrice Preço promocional do serviço
   * @returns O preço do serviço formatado
   */
  formatFinalPrice(
    price: number,
    isCostless: boolean,
    isPromotional: boolean,
    promotionalPrice: number
  ): string {
    if (isCostless) {
      return this.#translateService.instant('general-services.price.free');
    }
    if (isPromotional) {
      return this.#currencyPipe.transform(
        promotionalPrice,
        this.#translateService.currentLang
      );
    }
    if (!price) {
      return this.#translateService.instant('general-services.price.on-request');
    }
    return this.#currencyPipe.transform(price, this.#translateService.currentLang);
  }

  /**
   * Verifica se uma promoção está ativa com base nas datas e horários fornecidos
   * @param promotionStartDate Data de início da promoção
   * @param promotionEndDate Data de término da promoção
   * @param promotionStartHour Hora de início da promoção
   * @param promotionEndHour Hora de término da promoção
   * @param selectedDate Data selecionada
   * @param selectedHour Hora selecionada
   * @returns Indica se a promoção está ativa
   */
  isPromotionActive(
    promotionStartDate: string | null,
    promotionEndDate: string | null,
    promotionStartHour: number | null,
    promotionEndHour: number | null,
    selectedDate: Date,
    selectedHour: number
  ): boolean {
    if (!promotionStartDate || !promotionEndDate || promotionStartHour === null || promotionEndHour === null) {
      return false;
    }

    const promotionStart = new Date(promotionStartDate);
    const promotionEnd = new Date(promotionEndDate);

    const isDateWithinRange = selectedDate >= promotionStart && selectedDate <= promotionEnd;
    const isHourWithinRange = selectedHour >= promotionStartHour && selectedHour <= promotionEndHour;

    return isDateWithinRange && isHourWithinRange;
  }

  /**
   * Calcula o valor do imposto com base no preço do orçamento e no percentual do imposto
   * @param totalAmount Preço do orçamento
   * @param countryTaxRate Percentual do imposto
   * @returns O Final sem o imposto
   */
  calculateNetAmount(totalAmount: number, countryTaxRate: number): number {
    const netAmount = (totalAmount * 100) / (100 + countryTaxRate);
    return parseFloat(netAmount.toFixed(2));
  }
}
